import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { PlanId, PREMIUM_FEATURES, FeatureKey } from '@/constants/subscriptionPlans';
import { getPaddleInstance, usePaddle } from '@/hooks/usePaddle';

interface SubscriptionContextType {
  isSubscribed: boolean;
  isTrialing: boolean;
  isPaused: boolean;
  plan: PlanId | null;
  subscriptionEnd: Date | null;
  trialEnd: Date | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  hasFeature: (featureKey: FeatureKey) => boolean;
  openCheckout: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  pauseSubscription: () => Promise<void>;
  resumeSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const { user } = useAuth();
  usePaddle(); // Initialize Paddle.js once

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isTrialing, setIsTrialing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [plan, setPlan] = useState<PlanId | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<Date | null>(null);
  const [trialEnd, setTrialEnd] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  const checkSubscription = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const freshToken = sessionData?.session?.access_token;

    if (!freshToken) {
      setIsSubscribed(false);
      setIsTrialing(false);
      setIsPaused(false);
      setPlan(null);
      setSubscriptionEnd(null);
      setTrialEnd(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
        },
      });

      if (error) {
        console.error('Error checking subscription:', error);
        setIsSubscribed(false);
        setIsTrialing(false);
        setIsPaused(false);
        setPlan(null);
        return;
      }

      setIsSubscribed(data.subscribed || false);
      setIsTrialing(data.trialing || false);
      setIsPaused(data.paused || false);
      setPlan(data.plan || null);
      setSubscriptionEnd(data.subscription_end ? new Date(data.subscription_end) : null);
      setTrialEnd(data.trial_end ? new Date(data.trial_end) : null);
    } catch (err) {
      console.error('Error checking subscription:', err);
      setIsSubscribed(false);
      setIsTrialing(false);
      setIsPaused(false);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check subscription on auth state change
  useEffect(() => {
    if (user) {
      checkSubscription();
    } else {
      setIsSubscribed(false);
      setIsTrialing(false);
      setIsPaused(false);
      setPlan(null);
      setSubscriptionEnd(null);
      setTrialEnd(null);
      setLoading(false);
    }
  }, [user, checkSubscription]);

  // Auto-refresh subscription status every 5 minutes, only when tab is visible
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkSubscription();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const hasFeature = useCallback((featureKey: FeatureKey): boolean => {
    const feature = PREMIUM_FEATURES[featureKey];
    if (!feature) return true;

    if (isSubscribed && (plan === feature.requiredPlan || plan === 'pro')) {
      return true;
    }

    return false;
  }, [isSubscribed, plan]);

  const openCheckout = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const freshToken = sessionData?.session?.access_token;
    if (!freshToken) {
      console.error('No session for checkout');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
        },
      });

      if (error) throw error;

      // Open Paddle overlay checkout
      const paddle = getPaddleInstance();
      if (!paddle) {
        console.error('Paddle not initialized');
        return;
      }

      paddle.Checkout.open({
        items: [{ priceId: data.priceId, quantity: 1 }],
        customer: data.customerId ? { id: data.customerId } : undefined,
        customData: data.customData,
        settings: {
          successUrl: `${window.location.origin}/dashboard?subscription=success`,
        },
      });
    } catch (err) {
      console.error('Error creating checkout:', err);
    }
  }, []);

  const portalAction = useCallback(async (action: 'update_payment' | 'cancel' | 'pause' | 'resume') => {
    const { data: sessionData } = await supabase.auth.getSession();
    const freshToken = sessionData?.session?.access_token;
    if (!freshToken) return;

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: { Authorization: `Bearer ${freshToken}` },
        body: { action },
      });

      if (error) throw error;

      // For update_payment, open Paddle's payment update flow
      if (action === 'update_payment' && data.transactionId) {
        const paddle = getPaddleInstance();
        if (paddle) {
          paddle.Checkout.open({
            transactionId: data.transactionId,
          });
        }
      }

      // Refresh subscription state after any action
      await checkSubscription();
    } catch (err) {
      console.error(`Error performing portal action (${action}):`, err);
    }
  }, [checkSubscription]);

  const openCustomerPortal = useCallback(async () => {
    await portalAction('update_payment');
  }, [portalAction]);

  const pauseSubscription = useCallback(async () => {
    await portalAction('pause');
  }, [portalAction]);

  const resumeSubscription = useCallback(async () => {
    await portalAction('resume');
  }, [portalAction]);

  const value = {
    isSubscribed,
    isTrialing,
    isPaused,
    plan,
    subscriptionEnd,
    trialEnd,
    loading,
    checkSubscription,
    hasFeature,
    openCheckout,
    openCustomerPortal,
    pauseSubscription,
    resumeSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
